import React, { useEffect, useRef, useState } from "react";
import Sidebar from "./sidebar/Sidebar";
import ChatWindow from "./ChatWindow";
import ChatWindowHeader from "./chatWindowHeader/ChatWindowHeader";
import SideNavigation from "./sidebar/SideNavigation";
import Banner from "./banner/Banner";
import Publications from "./publications/Publications";
import ApprovalQueuePage from "../approval/ApprovalQueuePage";
import AdminManagementPage from "../admin/AdminManagementPage";
import { predefinedModels } from "./extensions/ExtensionCreation/ModelDropdown";
import "./ChatHome.css";
import Loader from "../general/Loader";

const ChatHome = () => {
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [selectedSection, setSelectedSection] = useState("chats"); 
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedChatObject, setSelectedChatObject] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPublications, setShowPublications] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    category: null,
    subCategory: null,
  });

  const [models, setModels] = useState(
    predefinedModels.filter((model) => !model.disabled).map((model) => model.value)
  );
  const [selectedModel, setSelectedModel] = useState(models[0]);

  useEffect(() => setSelectedModel(models[0]), [models]);

  useEffect(() => {
    console.log("setSelectedModel", selectedChatObject);
    setModels(predefinedModels.map((model) => model.value));
  }, [selectedChatObject]);

  const [pluginOptions, setPluginOptions] = useState({
    search_the_web_plugin_enabled: true,
    generate_picture_plugin_enabled: true,
    search_documents_plugin_enabled: true,
  });
  const chatWindowRef = useRef();
  const [oldChats, setOldChats] = useState([]);
  const sidebarRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  // Handler for search input change
  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  useEffect(() => {
    if (!selectedChatObject && oldChats.length > 0) {
      setSelectedChatObject(oldChats[0]); // Picks the latest chat
    }
  }, [oldChats, selectedChatObject]);

  const handleExtensionClick = (extension) => {
    setSelectedExtension(extension);
  };

  const handleSectionChange = (section) => {
    setIsSidebarCollapsed(false);
    if (section === "home") {
      setIsSidebarCollapsed(true);
    }
    setSelectedSection(section);
    if (section === "publications") {
      setShowPublications(true);
      setShowBanner(false);
    } else {
      setShowPublications(false);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const handleChatSelect = (selectedChat) => {
    console.log(selectedChat);
    setSelectedChatObject(selectedChat);
    setShowBanner(false);
    setSelectedSection("chats");
    setShowPublications(false);
  };

  const handleHomeClick = () => {
    setShowPublications(false);
    setSelectedSection("home");
    setShowBanner(true);
  };

  // const handleShowPublications = () => {
  //   setShowPublications(true);
  //   setShowBanner(false);
  //   setSelectedSection("publications");
  // };

  const handleClosePublications = () => {
    setShowPublications(false);
  };

  const handleFilterChange = (newFilters) => {
    setSelectedFilters(newFilters);
  };

  const handleStartNewChat = (extension) => {
    setSelectedChatObject(null);
    setIsCreatingChat(true); // Set flag to indicate chat creation
    if (sidebarRef.current) {
      sidebarRef.current.startNewChatFromExtension(extension);
    }
    setSelectedExtension(extension);
    setShowBanner(false);
  };

  const handleExtensionFetched = (fetchedExtension) => {
    setSelectedExtension(fetchedExtension);
  };

  return (
    <div className="app">
      <SideNavigation
        selectedSection={selectedSection}
        onSectionChange={handleSectionChange}
        toggleSidebar={handleSidebarToggle}
        isSidebarCollapsed={isSidebarCollapsed}
        handleHomeClick={handleHomeClick}
      />

      <div className="app__body">
        <Sidebar
          ref={sidebarRef}
          selectedSection={selectedSection}
          onExtensionClick={handleExtensionClick}
          isCollapsed={isSidebarCollapsed}
          onChatSelect={handleChatSelect}
          selectedChatId={selectedChatObject?.chatId}
          oldChats={oldChats}
          setOldChats={setOldChats}
          onChatsUpdate={() => {}}
          setChatId={(newChatId) =>
            setSelectedChatObject({ ...selectedChatObject, chatId: newChatId })
          }
          setSelectedChatObject={setSelectedChatObject}
          onShowBanner={() => setShowBanner(true)}
          onFilterChange={handleFilterChange}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          isCreatingChat={isCreatingChat}
          setIsCreatingChat={setIsCreatingChat}
        />

        <div className="main-content">
          {selectedSection === "approvals" ? (
            <ApprovalQueuePage />
          ) : selectedSection === "admin" ? (
            <AdminManagementPage />
          ) : showPublications ? (
            <Publications
              selectedFilters={selectedFilters}
              onChatSelect={handleChatSelect}
              onClose={handleClosePublications}
              searchValue={searchValue}
            />
          ) : (
            <>
              {selectedSection === "home" ? (
                <Banner
                  onStartNewChat={handleStartNewChat}
                  selectedSection={selectedSection}
                  setSelectedSection={setSelectedSection}
                  selectedFilters={selectedFilters}
                />
              ) : (
                <>
                  {selectedChatObject && !isCreatingChat ? (
                    <>
                      <ChatWindowHeader
                        extension={selectedExtension}
                        pluginOptions={pluginOptions}
                        setPluginOptions={setPluginOptions}
                        chatObject={selectedChatObject}
                        onExtensionFetched={handleExtensionFetched}
                        selectedModel={selectedModel}
                        setSelectedModel={setSelectedModel}
                        setModels={setModels}
                        models={models}
                        onGeneratePDF={() => chatWindowRef.current?.generatePDF()}
                      />
                      <ChatWindow
                        key={selectedChatObject.chatId} // Force re-mount with unique key
                        extension={selectedExtension}
                        chatId={selectedChatObject?.chatId}
                        pluginOptions={pluginOptions}
                        oldChats={oldChats}
                        setOldChats={setOldChats}
                        setSelectedSection={setSelectedSection}
                        handleSectionChange={handleSectionChange}
                        selectedModel={selectedModel}
                        setSelectedModel={setSelectedModel}
                        setModels={setModels}
                        models={models}
                        ref={chatWindowRef}
                        isCreatingChat={isCreatingChat}
                        setIsCreatingChat={setIsCreatingChat}
                        selectedChatObject={selectedChatObject}
                      />
                    </>
                  ) : (
                    isCreatingChat ? <Loader /> : null
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHome;